import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { format, isSameDay } from "date-fns";
import {
  Plus,
  Car,
  Truck,
  Repeat,
  Eye,
  UserCheck,
  Copy,
  AlertTriangle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import type { ShiftRow, HolidayRow } from "./types";
import { getShiftColour, driverDisplayName, calcWorkedHours } from "./utils";
import type { WorkTimeViolation } from "./workTimeRulesUtils";
import { loadVehicleTypes, getIconStyle, type VehicleTypeRow } from "@/lib/vehicleTypes";

// "Find Cover" only makes sense for genuine absences -- a shift that leaves
// a real gap in the roster. counts_as_worked_hours is an hours-accounting
// flag (e.g. dispatch/office duty isn't "driving hours") and is NOT a
// reliable signal for absence, so cover candidacy is based purely on the
// shift type code.
const NON_WORKING_CODES_SET = new Set([
  "LEAVE",
  "SICK",
  "UNAVAILABLE",
  "TIME_OFF",
  "TIMEOFF",
]);

const isCoverCandidateShift = (shift: ShiftRow): boolean => {
  const code = (shift.shift_type_code || "").toString().trim().toUpperCase();
  return NON_WORKING_CODES_SET.has(code);
};

const DRIVER_COLUMN_WIDTH = 210;
const DAY_COLUMN_WIDTH = 136;

const stringToColour = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  const colours = [
    "#3B82F6",
    "#EF4444",
    "#10B981",
    "#F59E0B",
    "#8B5CF6",
    "#EC4899",
    "#06B6D4",
    "#F97316",
    "#6366F1",
    "#14B8A6",
    "#E11D48",
    "#84CC16",
    "#0EA5E9",
    "#D946EF",
    "#22D3EE",
  ];

  return colours[Math.abs(hash) % colours.length];
};

const getInitials = (shift: ShiftRow): string => {
  if (shift.driver_firstname && shift.driver_lastname) {
    return (
      shift.driver_firstname[0] + shift.driver_lastname[0]
    ).toUpperCase();
  }

  const parts = shift.driver_name.split(/[\s_-]+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }

  return shift.driver_name.substring(0, 2).toUpperCase();
};

const formatTime12 = (hhmm: string): string => {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;

  return m === 0
    ? `${h12}:00 ${ampm}`
    : `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
};

const formatDuration = (decimalHours: number): string => {
  const totalMinutes = Math.round(decimalHours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;

  if (m === 0) return `${h}h`;
  return `${h}h ${m}min`;
};

const dayHasHoliday = (holidays: HolidayRow[] | undefined): boolean =>
  Array.isArray(holidays) && holidays.length > 0;

interface DriverWeekRow {
  driverName: string;
  displayName: string;
  initials: string;
  avatarColour: string;
  totalHours: number;
  shiftsByDay: Record<string, ShiftRow[]>;
}

interface WeekTimelineViewProps {
  shifts: ShiftRow[];
  rangeDays: Date[];
  holidaysByDate: Record<string, HolidayRow[]>;
  onEditShift: (shift: ShiftRow) => void;
  onCreateShift: (date?: Date) => void;
  onPublishDay: (dateStr: string) => void;
  onCopyAllocation: (dateStr: string) => void;
  showCancelled: boolean;
  onFindCover?: (shift: ShiftRow) => void;
  onShowTrends?: (dateStr: string) => void;
  selectedShiftIds: string[];
  onToggleShiftSelected: (shiftId: string, checked: boolean) => void;
  showCoverage: boolean;
  violationsByShiftId?: Record<string, WorkTimeViolation[]>;
}

const ShiftCellBar: React.FC<{
  shift: ShiftRow;
  onEdit: (shift: ShiftRow) => void;
  onFindCover?: (shift: ShiftRow) => void;
  selected?: boolean;
  onSelectedChange?: (shift: ShiftRow, checked: boolean) => void;
  vehicleTypes: VehicleTypeRow[];
  violations?: WorkTimeViolation[];
}> = ({
  shift,
  onEdit,
  onFindCover,
  selected = false,
  vehicleTypes,
  onSelectedChange,
  violations = [],
}) => {
  const colour = getShiftColour(shift);
  const isCancelled = shift.status === "cancelled";
  const isSTS = shift.shift_type_code?.toUpperCase().includes("STS");
  const workedH = calcWorkedHours(
    shift.start_hhmm,
    shift.end_hhmm,
    shift.break_minutes
  );
  const isDraft = shift.status === "draft";
  const isSpecial = !shift.counts_as_worked_hours;
  const needsCover = !isCancelled && isCoverCandidateShift(shift);

  const secondLine: string[] = [];
  if (shift.vehicle_type) secondLine.push(shift.vehicle_type);
  if (shift.shift_type_label) secondLine.push(shift.shift_type_label);

  const style: React.CSSProperties = isDraft
    ? {
        backgroundColor: `${colour}10`,
        border: `2px dashed ${colour}`,
      }
    : isSTS
    ? {
        backgroundColor: "transparent",
        border: `2px dashed ${colour}`,
      }
    : isSpecial
    ? {
        backgroundColor: `${colour}20`,
        border: `1px solid ${colour}66`,
      }
    : {
        backgroundColor: `${colour}CC`,
        border: `1px solid ${colour}`,
      };

  const primaryText =
    isDraft || isSTS || isSpecial ? colour : "#FFFFFF";
  const secondaryText =
    isDraft || isSTS || isSpecial
      ? `${colour}CC`
      : "rgba(255,255,255,0.72)";

  const breachesWorkTimeRules = !isCancelled && violations.length > 0;

  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        onEdit(shift);
      }}
      title={breachesWorkTimeRules ? violations.map((v) => v.message).join("\n") : undefined}
      className={`rounded-md px-2 py-1.5 cursor-pointer transition-all hover:brightness-110 hover:shadow-md group relative ${
        isCancelled ? "opacity-30" : ""
      } ${selected ? "ring-2 ring-blue-400/70" : ""} ${
        breachesWorkTimeRules ? "border-2 animate-work-time-breach" : ""
      }`}
      style={style}
    >
      {breachesWorkTimeRules && (
        <div className="absolute -top-1.5 -right-1.5 z-10 rounded-full bg-red-600 p-0.5 shadow" title={violations.map((v) => v.message).join("\n")}>
          <AlertTriangle className="h-2.5 w-2.5 text-white" />
        </div>
      )}

      {isDraft && onSelectedChange && (
        <div
          className="absolute left-1 top-1 z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={(checked) =>
              onSelectedChange(shift, !!checked)
            }
            className="bg-black/20 border-white/40"
          />
        </div>
      )}

      <div className={`flex items-center gap-1 ${isDraft ? "pl-6" : ""}`}>
        <span
          className="text-[11px] font-bold truncate"
          style={{ color: primaryText }}
        >
          {formatTime12(shift.start_hhmm)} - {formatTime12(shift.end_hhmm)}
        </span>
        <span
          className="text-[10px] font-medium whitespace-nowrap"
          style={{ color: secondaryText }}
        >
          • {formatDuration(workedH)}
        </span>
        {shift.repeat_group_id && (
          <Repeat className="h-2.5 w-2.5 shrink-0 ml-auto text-purple-400" />
        )}
      </div>

      <div
        className={`flex items-center gap-1 mt-0.5 ${isDraft ? "pl-6" : ""}`}
      >
        {isDraft && (
          <span className="inline-flex items-center gap-1 text-[9px] italic text-amber-400">
            <Eye className="h-2.5 w-2.5" />
            Draft
          </span>
        )}

        {shift.vehicle_number && (
          <>
            {getIconStyle(shift.vehicle_type, vehicleTypes) === "truck" ? (
              <Truck
                className="h-2.5 w-2.5 shrink-0"
                style={{ color: secondaryText }}
              />
            ) : (
              <Car
                className="h-2.5 w-2.5 shrink-0"
                style={{ color: secondaryText }}
              />
            )}
          </>
        )}

        <span
          className="text-[10px] truncate"
          style={{ color: secondaryText }}
        >
          {secondLine.join(" • ")}
          {shift.vehicle_number ? ` • ${shift.vehicle_number}` : ""}
        </span>
      </div>

      {needsCover && onFindCover && (
        <div className={`mt-1 flex items-center ${isDraft ? "pl-6" : ""}`}>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onFindCover(shift);
            }}
            className="inline-flex items-center gap-1 text-[10px] font-semibold px-1.5 py-0.5 rounded bg-emerald-600/90 hover:bg-emerald-600 text-white shadow-sm"
            title="Find a cover driver for this absence"
          >
            <UserCheck className="h-2.5 w-2.5" />
            Find Cover
          </button>
        </div>
      )}
    </div>
  );
};

const WeekTimelineView: React.FC<WeekTimelineViewProps> = ({
  shifts,
  rangeDays,
  holidaysByDate,
  onEditShift,
  onCreateShift,
  onPublishDay,
  onCopyAllocation,
  showCancelled,
  onFindCover,
  onShowTrends,
  selectedShiftIds,
  onToggleShiftSelected,
  showCoverage,
  violationsByShiftId,
}) => {
  const today = new Date();
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);
  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);
  }, []);

  // The "Driver" header row sticks just below the "Coverage" row, so it needs
  // to know that row's REAL rendered height -- a hardcoded guess drifts
  // whenever the coverage row wraps to another line (long holiday names,
  // font/zoom differences, etc), which used to let the sticky headers
  // overlap the first driver row's shift blocks. Measured live via
  // ResizeObserver instead of assumed.
  const coverageRowRef = useRef<HTMLTableRowElement>(null);
  const [coverageRowHeight, setCoverageRowHeight] = useState(82);

  useLayoutEffect(() => {
    if (!showCoverage) return;
    const el = coverageRowRef.current;
    if (!el) return;

    const measure = () => setCoverageRowHeight(el.getBoundingClientRect().height);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [showCoverage]);

  const filteredVisibleShifts = useMemo(() => {
    return showCancelled
      ? shifts
      : shifts.filter((s) => s.status !== "cancelled");
  }, [shifts, showCancelled]);

  const driverRows: DriverWeekRow[] = useMemo(() => {
    const map = new Map<string, { shifts: ShiftRow[]; first: ShiftRow }>();

    for (const s of filteredVisibleShifts) {
      const existing = map.get(s.driver_name);
      if (existing) {
        existing.shifts.push(s);
      } else {
        map.set(s.driver_name, { shifts: [s], first: s });
      }
    }

    const rows: DriverWeekRow[] = [];

    for (const [driverName, data] of map.entries()) {
      const shiftsByDay: Record<string, ShiftRow[]> = {};
      let totalHours = 0;

      for (const day of rangeDays) {
        const dateStr = format(day, "yyyy-MM-dd");
        shiftsByDay[dateStr] = [];
      }

      for (const s of data.shifts) {
        if (shiftsByDay[s.shift_date]) {
          shiftsByDay[s.shift_date].push(s);
        }

        if (s.status !== "cancelled") {
          totalHours += s.worked_hours || 0;
        }
      }

      for (const key of Object.keys(shiftsByDay)) {
        shiftsByDay[key].sort((a, b) =>
          a.start_hhmm.localeCompare(b.start_hhmm)
        );
      }

      rows.push({
        driverName,
        displayName: driverDisplayName(data.first),
        initials: getInitials(data.first),
        avatarColour: stringToColour(driverName),
        totalHours,
        shiftsByDay,
      });
    }

    rows.sort((a, b) =>
      a.displayName.toLowerCase().localeCompare(b.displayName.toLowerCase())
    );

    return rows;
  }, [filteredVisibleShifts, rangeDays]);

  const coverageByDay = useMemo(() => {
    return rangeDays.map((day) => {
      const dateStr = format(day, "yyyy-MM-dd");
      const dayShifts = filteredVisibleShifts.filter(
        (s) => s.shift_date === dateStr && s.status !== "cancelled"
      );

      const uniqueDrivers = new Set(dayShifts.map((s) => s.driver_name)).size;
      const totalHours = dayShifts.reduce(
        (sum, s) => sum + Number(s.worked_hours || 0),
        0
      );

      return {
        dateStr,
        uniqueDrivers,
        totalHours,
      };
    });
  }, [filteredVisibleShifts, rangeDays]);

  const stickyDateTop = showCoverage ? coverageRowHeight : 0;

  // Solid, fully opaque backgrounds so scrolled content does NOT bleed through
  // the frozen header / driver column.
  // NOTE: z-indices are intentionally kept BELOW the shadcn Dialog layer (z-50),
  // otherwise sticky cells punch through any modal/overlay that opens on top
  // of the roster (e.g. the Demand Trends overlay).
  const HEADER_BG = "#0f1420"; // near-black solid
  const HEADER_BG_HOLIDAY = "#2a1a0d";
  const HEADER_BG_TODAY = "#0d1a2d";
  const DRIVER_CELL_BG = "#0f1420";

  return (
    <div className="border border-gray-700 rounded-lg overflow-hidden bg-gray-900/60">
      <div className="overflow-x-auto overflow-y-auto max-h-[calc(100vh-260px)] relative">
        <table
          className="border-collapse w-full"
          style={{
            tableLayout: "fixed",
            minWidth: `${DRIVER_COLUMN_WIDTH + rangeDays.length * DAY_COLUMN_WIDTH}px`,
            borderSpacing: 0,
          }}
        >
          <thead>
            {showCoverage && (
              <tr ref={coverageRowRef}>
                <th
                  className="border-b border-r border-gray-700 p-2 text-left"
                  style={{
                    position: "sticky",
                    top: 0,
                    left: 0,
                    zIndex: 30,
                    background: HEADER_BG,
                    width: `${DRIVER_COLUMN_WIDTH}px`,
                    minWidth: `${DRIVER_COLUMN_WIDTH}px`,
                  }}
                >
                  <div className="text-xs font-medium text-gray-400">
                    Coverage
                  </div>
                </th>

                {coverageByDay.map((dayCoverage) => {
                  const dayHolidays = holidaysByDate[dayCoverage.dateStr] || [];
                  const hasHoliday = dayHasHoliday(dayHolidays);

                  return (
                    <th
                      key={`coverage-${dayCoverage.dateStr}`}
                      className="border-b border-r border-gray-700 p-2 text-center"
                      style={{
                        position: "sticky",
                        top: 0,
                        zIndex: 25,
                        background: hasHoliday ? HEADER_BG_HOLIDAY : HEADER_BG,
                        width: `${DAY_COLUMN_WIDTH}px`,
                        minWidth: `${DAY_COLUMN_WIDTH}px`,
                      }}
                    >
                      <div className="flex flex-col items-center gap-1">
                        <div className="text-[10px] uppercase tracking-wider text-emerald-300 font-semibold">
                          COVERAGE
                        </div>
                        <div className="text-[11px] text-gray-200">
                          {dayCoverage.uniqueDrivers} drivers
                        </div>
                        <div className="text-[11px] text-gray-400">
                          {formatDuration(dayCoverage.totalHours)}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            )}


            <tr>
              <th
                className="border-b border-r border-gray-700 p-2 text-left"
                style={{
                  position: "sticky",
                  top: `${stickyDateTop}px`,
                  left: 0,
                  zIndex: 30,
                  background: HEADER_BG,
                  width: `${DRIVER_COLUMN_WIDTH}px`,
                  minWidth: `${DRIVER_COLUMN_WIDTH}px`,
                }}
              >
                <div className="text-xs font-medium text-gray-400">
                  Driver
                </div>
              </th>

              {rangeDays.map((day) => {
                const dateStr = format(day, "yyyy-MM-dd");
                const isTodayCol = isSameDay(day, today);
                const dayHolidays = holidaysByDate[dateStr] || [];
                const hasHoliday = dayHasHoliday(dayHolidays);
                const hasDrafts = filteredVisibleShifts.some(
                  (s) => s.shift_date === dateStr && s.status === "draft"
                );

                const bg = hasHoliday
                  ? HEADER_BG_HOLIDAY
                  : isTodayCol
                  ? HEADER_BG_TODAY
                  : HEADER_BG;

                return (
                  <th
                    key={dateStr}
                    className="border-b border-r border-gray-700 p-0 text-center"
                    style={{
                      position: "sticky",
                      top: `${stickyDateTop}px`,
                      zIndex: 25,
                      background: bg,
                      width: `${DAY_COLUMN_WIDTH}px`,
                      minWidth: `${DAY_COLUMN_WIDTH}px`,
                    }}
                  >

                    {hasHoliday && (
                      <div className="bg-orange-500/20 border-b border-orange-500/40 px-2 py-1">
                        <div className="text-[10px] font-semibold uppercase tracking-wider text-orange-300 truncate">
                          {dayHolidays
                            .map((h) => h.holiday_name)
                            .join(" • ")}
                        </div>
                      </div>
                    )}

                    <div className="px-2 py-2">
                      <div className="flex flex-col items-center gap-0.5">
                        <span
                          className={`text-[10px] uppercase tracking-wider font-medium ${
                            isTodayCol ? "text-blue-400" : "text-gray-500"
                          }`}
                        >
                          {format(day, "EEE")}
                        </span>
                        <span
                          className={`text-lg font-bold ${
                            isTodayCol ? "text-blue-300" : "text-white"
                          }`}
                        >
                          {format(day, "d")}
                        </span>
                      </div>

                      <div className="flex items-center justify-center gap-1 mt-2 flex-wrap">
                        {hasDrafts && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-6 text-[10px] px-2 border-green-700 text-green-400 hover:bg-green-900/30"
                            onClick={() => onPublishDay(dateStr)}
                          >
                            Publish Day
                          </Button>
                        )}

                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 text-[10px] px-2 text-gray-400 hover:text-blue-300"
                          onClick={() => onCopyAllocation(dateStr)}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          Copy
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-cyan-400 hover:text-cyan-300"
                          onClick={() => onShowTrends?.(dateStr)}
                          title="Show trends"
                        >
                          <span className="text-[10px] font-bold">T</span>
                        </Button>

                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-6 w-6 p-0 text-gray-400 hover:text-blue-300"
                          onClick={() => onCreateShift(day)}
                        >
                          <Plus className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {driverRows.map((row) => (
              <tr
                key={row.driverName}
                className="group/row hover:bg-gray-800/20"
              >
                <td
                  className="border-b border-r border-gray-700/60 p-2"
                  style={{
                    position: "sticky",
                    left: 0,
                    zIndex: 20,
                    background: DRIVER_CELL_BG,
                    width: `${DRIVER_COLUMN_WIDTH}px`,
                    minWidth: `${DRIVER_COLUMN_WIDTH}px`,
                  }}
                >
                  <div className="flex items-center gap-2.5">
                    <div
                      className="shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold"
                      style={{ backgroundColor: row.avatarColour }}
                    >
                      {row.initials}
                    </div>

                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-white truncate leading-tight">
                        {row.displayName}
                      </p>
                      <p className="text-[11px] text-gray-400 leading-tight truncate">
                        {formatDuration(row.totalHours)} • $0.00
                      </p>
                    </div>
                  </div>
                </td>

                {rangeDays.map((day) => {
                  const dateStr = format(day, "yyyy-MM-dd");
                  const dayShifts = row.shiftsByDay[dateStr] || [];
                  const isTodayCol = isSameDay(day, today);
                  const dayHolidays = holidaysByDate[dateStr] || [];
                  const hasHoliday = dayHasHoliday(dayHolidays);

                  return (
                    <td
                      key={dateStr}
                      className={`border-b border-r border-gray-700/40 p-1 align-top ${
                        hasHoliday
                          ? "bg-orange-950/15"
                          : isTodayCol
                          ? "bg-blue-950/10"
                          : ""
                      }`}
                      style={{ width: `${DAY_COLUMN_WIDTH}px`, minWidth: `${DAY_COLUMN_WIDTH}px` }}
                    >
                      <div className="space-y-1 min-h-[48px]">
                        {dayShifts.map((shift) => (
                          <ShiftCellBar
                            key={shift.id}
                            shift={shift}
                            onEdit={onEditShift}
                            onFindCover={onFindCover}
                            vehicleTypes={vehicleTypes}
                            selected={selectedShiftIds.includes(shift.id)}
                            onSelectedChange={(shiftObj, checked) =>
                              onToggleShiftSelected(shiftObj.id, checked)
                            }
                            violations={violationsByShiftId?.[shift.id]}
                          />
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default WeekTimelineView;
